import { useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { AnalyticsContext, AnalyticsEventName } from '@/analytics/constants';
import { useAnalytics } from '@/analytics/hooks/useAnalytics';

const HOTJAR_EVENT_NAME = 'creditplan_feedback';
const HOTJAR_RETRY_INTERVAL_MS = 250;
const HOTJAR_MAX_WAIT_MS = 5000;
const CREDIT_PLAN_FEEDBACK_PENDING_KEY = 'creditplan_feedback_pending';
const CREDIT_PLAN_FEEDBACK_EVENT = 'creditplan-feedback-pending';

const CreditPlanFeedbackResolver = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const { trackEvent } = useAnalytics();
  const hasInitiatedLogin = useRef(false);
  const hasTrackedView = useRef(false);
  const hasTriggeredSurvey = useRef(false);

  const planId = searchParams.get('planId')?.trim() || '';
  const jurisdiction = searchParams.get('jurisdiction')?.trim() || 'unknown';

  useEffect(() => {
    if (auth.isLoading || !!auth.activeNavigator || auth.isAuthenticated || hasInitiatedLogin.current) {
      return;
    }

    hasInitiatedLogin.current = true;
    const returnUrl = `${window.location.pathname}${window.location.search}`;

    console.info('[CreditPlanFeedback] User not authenticated. Redirecting to login.');
    auth.signinRedirect({
      state: {
        returnUrl,
        arg: undefined,
        endpoint: undefined
      }
    });
  }, [auth]);

  const triggerSurvey = useCallback(() => {
    if (typeof window === 'undefined' || typeof window.hj !== 'function') {
      console.info('[CreditPlanFeedback] Hotjar not ready yet. Survey should be displayed when available.');
      return false;
    }

    window.hj('event', HOTJAR_EVENT_NAME);
    console.info('[CreditPlanFeedback] Hotjar survey displayed (event sent).');
    return true;
  }, []);

  useEffect(() => {
    if (!auth.isAuthenticated) return;

    if (!hasTrackedView.current) {
      trackEvent(AnalyticsEventName.FeedbackCreditplan, {
        context: AnalyticsContext.CreditPlanEmailLink,
        jurisdiction,
        planId: planId || undefined
      });
      hasTrackedView.current = true;
    }

    if (!hasTriggeredSurvey.current) {
      hasTriggeredSurvey.current = true;

      const startedAt = Date.now();
      const intervalId = window.setInterval(() => {
        if (triggerSurvey()) {
          window.clearInterval(intervalId);
          return;
        }

        if (Date.now() - startedAt >= HOTJAR_MAX_WAIT_MS) {
          console.info('[CreditPlanFeedback] Timeout while waiting for Hotjar on feedback resolver.');
          window.clearInterval(intervalId);
        }
      }, HOTJAR_RETRY_INTERVAL_MS);

      return () => {
        window.clearInterval(intervalId);
      };
    }
  }, [auth.isAuthenticated, jurisdiction, planId, trackEvent, triggerSurvey]);

  useEffect(() => {
    if (!auth.isAuthenticated) return;

    if (!hasTriggeredSurvey.current || !triggerSurvey()) {
      sessionStorage.setItem(CREDIT_PLAN_FEEDBACK_PENDING_KEY, '1');
      window.dispatchEvent(new Event(CREDIT_PLAN_FEEDBACK_EVENT));
      console.info('[CreditPlanFeedback] Survey pending. Redirecting to home to display on main site.');
    }

    if (window.location.pathname === '/') return;
    navigate('/', { replace: true });
  }, [auth.isAuthenticated, navigate, triggerSurvey]);

  return null;
};

export default CreditPlanFeedbackResolver;
